import { getCountryCode } from "@/utils/common-functions";
import { ShippingItem } from "@/@types/ShippingItem";

/**
 * NEXT-GUEST-QUOTES-V2 — request/response mappers for `POST /v2/rating/quotes/guest`.
 *
 * The v2 wire contract (ShipvistaAPI 06-rating.md §6.1, verified vs main 2026-07-19) is a clean
 * break from the v1 `/Rate/Guest` shape:
 *   - camelCase JSON (System.Text.Json web defaults)
 *   - metric-only pieces: `{ lengthCm, widthCm, heightCm, weightG }` (v1 carried `unitOfMeasurement`
 *     and sent raw L/W/H/weight — the server stored metric; the unit was a UI concern. v2 makes that
 *     explicit: pieces are ALWAYS metric on the wire.)
 *   - `Money` is integer cents + currency: `{ cents, currency }` (v1 used decimal `amount`)
 *   - enums are strings: `insuranceType: "NONE" | "InsureShield" | "Insurevista" | "CarrierDeclared"`,
 *     `currency: "CAD" | "USD"`
 *
 * This file does NOT port v1-isms. The v1 `object-mapper` library (PascalCase keys, `lineItems`,
 * `FromAddress`/`ToAddress`, decimal `declaredValue.amount`) is intentionally gone — v2 has its own
 * shape and a hand-written mapper is clearer than fighting `object-mapper` into camelCase.
 */

// ── v2 wire types (the body for POST /v2/rating/quotes/guest) ─────────────────

/** v2 `Money` (Shipvista.Platform.Money) — integer cents + ISO currency string. */
export interface V2Money {
  cents: number;
  currency: "CAD" | "USD";
}

/** v2 `InsuranceType` enum (Shipvista.Modules.Shipping.Contracts) as serialized by the string-enum
 *  converter — the literal .NET member names: None, Insurevista, InsureShield, CarrierDeclared. */
export type V2InsuranceType =
  | "None"
  | "Insurevista"
  | "InsureShield"
  | "CarrierDeclared";

/** v2 `CarrierAddress` (Shipping.Contracts) — ISO country + sub-division codes, no province name. */
export interface V2CarrierAddress {
  company: string | null;
  name: string;
  street1: string;
  street2: string | null;
  city: string;
  countryCode: string;
  stateCode: string | null;
  postalCode: string;
  phone: string | null;
  email: string | null;
  isResidence: boolean;
}

/** v2 `LineItem` (Shipping.Contracts) — metric dimensions + declared value + insurance. */
export interface V2LineItem {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  weightG: number;
  declaredValue: V2Money | null;
  insuranceType: V2InsuranceType;
  packagingType: string | null;
  serviceOptions: string[];
}

export interface V2QuoteRequestBody {
  from: V2CarrierAddress;
  to: V2CarrierAddress;
  pieces: V2LineItem[];
  /** Carrier allow-list filter; null = all active accounts. The guest tenant owns the house
   *  carrier accounts an anonymous quote is allowed to see, so null is the right default here. */
  carrierAllowList: string[] | null;
}

// ── request mapping ──────────────────────────────────────────────────────────

/**
 * Map the guest-quotes UI's `unitOfMeasurement` to the multiplier that converts the form's current
 * unit into metric. The form toggles METRIC/IMPERIAL and rewrites its own values on toggle, so by
 * the time `packageFormSubmit`/`ltlFreightFormSubmit` run, the L/W/H/weight strings ARE in the
 * selected unit — we must normalize them to cm/g for v2.
 *
 * Dimensions: IMPERIAL→METRIC is `* 2.54` (in→cm); METRIC is already cm. Weight: IMPERIAL→METRIC is
 * `* 2.2` (the form's lb→kg convention); METRIC is already kg → grams is `* 1000`.
 *
 * (Mirrors the conversion already living in packaging-items.tsx / freight-items.tsx useEffects.)
 */
const DIM_FACTOR = 2.54; // inches → cm
const LB_TO_KG = 2.2; // lb → kg (the form's convention)
const KG_TO_G = 1000;

function toMetricDimensions(
  raw: { length?: string; width?: string; height?: string },
  unit: string
): { lengthCm: number; widthCm: number; heightCm: number } {
  const factor = unit === "IMPERIAL" ? DIM_FACTOR : 1;
  const num = (v: string | undefined) =>
    v && !Number.isNaN(parseFloat(v)) ? parseFloat(v) : 0;
  return {
    lengthCm: num(raw.length) * factor,
    widthCm: num(raw.width) * factor,
    heightCm: num(raw.height) * factor,
  };
}

function toMetricWeightG(rawWeight: string, unit: string): number {
  const w =
    rawWeight && !Number.isNaN(parseFloat(rawWeight))
      ? parseFloat(rawWeight)
      : 0;
  const kg = unit === "IMPERIAL" ? w * LB_TO_KG : w;
  return kg * KG_TO_G;
}

/**
 * Map a v1 UI InsuranceType code (`NONE` / `INSURESHIELD` / `INSUREVISTA` / `CARRIER_DECLARED`) to
 * the v2 enum member name the wire expects. Unknown/missing → `None`.
 */
function mapInsuranceType(code?: string): V2InsuranceType {
  const key = (code ?? "NONE").toUpperCase();
  switch (key) {
    case "INSURESHIELD":
      return "InsureShield";
    case "INSUREVISTA":
      return "Insurevista";
    case "CARRIER_DECLARED":
    case "CARRIERDECLARED":
      return "CarrierDeclared";
    default:
      return "None";
  }
}

/** Build a v2 declared-value `Money` (integer cents) from a UI amount + currency. */
function toDeclaredValue(
  amount: string | number | undefined,
  currency: "CAD" | "USD"
): V2Money | null {
  if (amount === undefined || amount === null || `${amount}`.trim() === "")
    return null;
  const dollars = parseFloat(`${amount}`);
  if (Number.isNaN(dollars) || dollars <= 0) return null;
  // Integer cents (banker's rounding on the server side; round-half-up is fine for the request —
  // the server re-derives the charged amount from the rated quote, not from this input).
  return { cents: Math.round(dollars * 100), currency };
}

/**
 * Build the `POST /v2/rating/quotes/guest` body from the guest-quotes form payload. The UI collects
 * N `shippingItems` (each with an optional `quantity`); v2 wants one `LineItem` per physical piece,
 * so a quantity of 3 fans out to 3 identical pieces (mirrors the v1 mapper's per-piece expansion).
 */
export function mapGuestQuoteRequest(form: {
  shipFrom: any;
  shipTo: any;
  shippingItems: ShippingItem[];
  unitOfMeasurement: string;
  carrierAllowList?: string[] | null;
}): V2QuoteRequestBody {
  const { shipFrom, shipTo, shippingItems, unitOfMeasurement } = form;

  const countryCode = (c: string | undefined) => {
    const code = c ? getCountryCode(c) : "";
    return code || (c ?? "").toUpperCase();
  };

  const from: V2CarrierAddress = {
    company: null,
    name: shipFrom?.name ?? "ShipVista",
    street1: shipFrom?.streetAddress ?? "",
    street2: shipFrom?.streetAddress2 ?? shipFrom?.streetAddres2 ?? null,
    city: shipFrom?.city ?? "",
    countryCode: countryCode(shipFrom?.country),
    stateCode: shipFrom?.stateCode ?? null,
    postalCode: shipFrom?.postCode ?? "",
    phone: shipFrom?.phone ?? null,
    email: shipFrom?.email ?? null,
    isResidence: !!shipFrom?.isResidence,
  };

  const to: V2CarrierAddress = {
    company: null,
    name: shipTo?.name ?? "Client",
    street1: shipTo?.streetAddress ?? "",
    street2: shipTo?.streetAddress2 ?? shipTo?.streetAddres2 ?? null,
    city: shipTo?.city ?? "",
    countryCode: countryCode(shipTo?.country),
    stateCode: shipTo?.stateCode ?? null,
    postalCode: shipTo?.postCode ?? "",
    phone: shipTo?.phone ?? null,
    email: shipTo?.email ?? null,
    isResidence: !!shipTo?.isResidence,
  };

  // Fan out each line by its quantity → one v2 piece per physical parcel (v1 precedent). The
  // currency for a declared value is CAD by default (the guest tenant's base currency); the UI
  // does not collect a per-piece currency today.
  const pieces: V2LineItem[] = [];
  for (const item of shippingItems) {
    const dims = toMetricDimensions(item, unitOfMeasurement);
    const weightG = toMetricWeightG(`${item.weight ?? "0"}`, unitOfMeasurement);
    const insuranceType = mapInsuranceType(item.InsuranceType?.code);
    const declaredValue = toDeclaredValue(item.insurance, "CAD");
    const packagingType = item.package?.code ?? null;

    const qty = Math.max(1, parseInt(`${item.quantity ?? "1"}`, 10) || 1);
    for (let i = 0; i < qty; i++) {
      pieces.push({
        ...dims,
        weightG,
        declaredValue,
        insuranceType,
        packagingType,
        serviceOptions: [],
      });
    }
  }

  return {
    from,
    to,
    pieces,
    carrierAllowList: form.carrierAllowList ?? null,
  };
}

// ── response mapping ─────────────────────────────────────────────────────────

/** v2 `ChargeLine` wire shape (Shipping.Contracts). */
export interface V2ChargeLine {
  code: string;
  name: string;
  amount: V2Money;
}

/** v2 `PricedQuote` wire shape (Rating.Contracts) — one carrier service's commissioned quote. */
export interface V2PricedQuote {
  carrierCode: string;
  serviceCode: string;
  serviceName: string;
  baseCharge: V2Money;
  adjustments: V2ChargeLine[];
  taxes: V2ChargeLine[];
  insuranceLine: V2ChargeLine | null;
  commissionLine: V2ChargeLine | null;
  totalCharge: V2Money;
  /** Guest-only display markup (the +80% compare-at); null for authenticated `/quotes`. */
  regularCompareAtCharge: V2Money | null;
  currency: "CAD" | "USD";
  carrierAccountId: string;
  /** GLM-72 InsureShield pre-flight quote id (not present on all quotes). */
  insuranceQuoteId?: string | null;
}

/** v2 `CarrierQuoteError` — a per-carrier failure riding in-band inside a 200 (§6.3 point 4). */
export interface V2CarrierQuoteError {
  carrierCode: string;
  code: string;
  detail: string;
  timedOut: boolean;
}

/** v2 `RateShopResult` — the 200 body for `/quotes` and `/quotes/guest`. */
export interface V2RateShopResult {
  quotes: V2PricedQuote[];
  carrierErrors: V2CarrierQuoteError[];
}

// ── cents → display decimal ──────────────────────────────────────────────────

/**
 * Convert v2 integer cents to a 2-decimal display amount. The UI's `AvailableRate.price` fields
 * (`totalCharge`, `baseCharge`, line amounts) are display-only — they render in the rates table and
 * are never re-sent to the backend, so a decimal here is fine (the v1 response already used
 * decimals for display). Storage/arithmetic uses the cents value carried alongside.
 */
export function centsToDecimal(cents: number | null | undefined): number {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return 0;
  return Math.round(cents) / 100;
}
